import { Component, ChangeDetectorRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PlanningTopNavComponent } from '../../../shared/planning-nav/planning-top-nav.component';


type CalendarDay = {
  date: Date;
  inMonth: boolean;
  isToday: boolean;
};

type WeekRow = {
  weekNumber: number;
  days: CalendarDay[]; // length 7
};

type MonthView = {
  year: number;
  monthIndex: number; // 0-11
  label: string;
  weeks: WeekRow[];
};

@Component({
  selector: 'app-weekly-calendar',
  standalone: true,
  imports: [CommonModule, FormsModule, PlanningTopNavComponent],
  templateUrl: './weekly-calendar.component.html',
  styleUrls: ['./weekly-calendar.component.scss'],
})
export class WeeklyCalendarComponent implements OnInit {
  // UI
  monthNames = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
  ];
  weekDayShort = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  yearOptions: number[] = [];

  // selected month/year (controlled by dropdowns)
  selectedYear!: number;
  selectedMonthIndex!: number;

  // rendered month page
  view!: MonthView;

  constructor(private readonly cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    const now = new Date();
    const thisYear = now.getFullYear();

    // you can widen this later if you want
    this.yearOptions = Array.from({ length: 11 }, (_, i) => thisYear - 5 + i);

    this.selectedYear = thisYear;
    this.selectedMonthIndex = now.getMonth();

    this.buildView();
  }

  // ----------------------------
  // Pagination (NO SCROLL)
  // ----------------------------
  prevMonth(): void {
    // January -> December previous year
    if (this.selectedMonthIndex === 0) {
      this.selectedMonthIndex = 11;
      this.selectedYear -= 1;
    } else {
      this.selectedMonthIndex -= 1;
    }

    // keep year dropdown sane if you roll outside the list
    this.ensureYearInOptions(this.selectedYear);
    this.buildView();
  }

  nextMonth(): void {
    // December -> January next year
    if (this.selectedMonthIndex === 11) {
      this.selectedMonthIndex = 0;
      this.selectedYear += 1;
    } else {
      this.selectedMonthIndex += 1;
    }

    this.ensureYearInOptions(this.selectedYear);
    this.buildView();
  }

  onMonthJump(): void {
    this.buildView();
  }

  onYearChange(): void {
    this.buildView();
  }

  // ----------------------------
  // Build Month View
  // ----------------------------
  private buildView(): void {
    const year = this.selectedYear;
    const monthIndex = this.selectedMonthIndex;

    this.view = {
      year,
      monthIndex,
      label: this.monthNames[monthIndex],
      weeks: this.buildWeeks(year, monthIndex),
    };

    // if you're on OnPush elsewhere, this makes updates immediate
    this.cd.detectChanges();
  }

  private buildWeeks(year: number, monthIndex: number): WeekRow[] {
    const today = this.startOfDay(new Date());

    const firstOfMonth = new Date(year, monthIndex, 1);
    const lastOfMonth = new Date(year, monthIndex + 1, 0);

    // Calendar starts on Monday
    const start = this.startOfWeekMonday(firstOfMonth);
    const end = this.endOfWeekSunday(lastOfMonth);

    const weeks: WeekRow[] = [];

    let cursor = new Date(start);
    while (cursor <= end) {
      const weekStart = new Date(cursor);

      const days: CalendarDay[] = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(weekStart);
        d.setDate(weekStart.getDate() + i);

        days.push({
          date: d,
          inMonth: d.getMonth() === monthIndex,
          isToday: this.isSameDay(d, today),
        });
      }

      weeks.push({
        weekNumber: this.isoWeekNumber(weekStart),
        days,
      });

      cursor.setDate(cursor.getDate() + 7);
    }

    return weeks;
  }

  // ----------------------------
  // Helpers (dates)
  // ----------------------------
  formatDayNumber(d: Date): number {
    return d.getDate();
  }

  private startOfDay(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  private isSameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  private startOfWeekMonday(d: Date): Date {
    const x = this.startOfDay(d);
    // JS: 0=Sun..6=Sat. Convert to Monday=0..Sunday=6
    const day = (x.getDay() + 6) % 7;
    x.setDate(x.getDate() - day);
    return x;
  }

  private endOfWeekSunday(d: Date): Date {
    const x = this.startOfDay(d);
    const day = (x.getDay() + 6) % 7; // Monday=0..Sunday=6
    x.setDate(x.getDate() + (6 - day));
    return x;
  }

  // ISO week number
  private isoWeekNumber(d: Date): number {
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = date.getUTCDay() || 7; // Mon=1..Sun=7
    date.setUTCDate(date.getUTCDate() + 4 - dayNum); // nearest Thursday
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    return weekNo;
  }

  private ensureYearInOptions(year: number): void {
    if (this.yearOptions.includes(year)) return;

    // Expand the range to include the new year while keeping it nice
    const min = Math.min(...this.yearOptions, year);
    const max = Math.max(...this.yearOptions, year);
    const span = Math.max(11, max - min + 1);

    const start = Math.floor((min + max - (span - 1)) / 2);
    this.yearOptions = Array.from({ length: span }, (_, i) => start + i);
  }
}
